'use client';

/**
 * KYC Hook
 *
 * Provides KYC (Know Your Customer) management functionality for customers.
 * Fetches policies, cases, and requirements from Route Royal's KYC module.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useCustomerAuth } from '@/components/auth/CustomerAuthProvider';
import type {
  KycCase,
  KycCaseStatus,
  KycPolicy,
  KycRequirement,
  KycRequirementType,
  RequestKycUploadUrlData,
  CompleteKycUploadData,
  KycUploadUrlResponse,
} from '@/types/customer';

// ============================================================================
// Types
// ============================================================================

export interface UseKycReturn {
  /** All KYC cases for the current customer */
  cases: KycCase[];
  /** The current/active KYC case (most recent non-terminal) */
  currentCase: KycCase | null;
  /** Available KYC policies for this organisation */
  policies: KycPolicy[];
  /** Whether at least one active policy exists */
  hasAvailablePolicy: boolean;
  /** All requirements for the current case */
  requirements: KycRequirement[];
  /** Document-type requirements */
  documentRequirements: KycRequirement[];
  /** Verification/check-type requirements */
  checkRequirements: KycRequirement[];
  /** Attribute-type requirements */
  attributeRequirements: KycRequirement[];
  /** Number of completed (verified/waived) requirements */
  completedRequirements: number;
  /** Total number of requirements */
  totalRequirements: number;
  /** Whether cases/policies are loading */
  isLoading: boolean;
  /** Whether a mutation is in progress */
  isSubmitting: boolean;
  /** Error if any */
  error: string | null;
  /** Refresh KYC cases and requirements */
  refresh: () => Promise<void>;
  /** Start a new KYC case (auto-selects policy if only one) */
  startCase: (policyId?: string) => Promise<KycCase | null>;
  /** Submit a KYC case for review */
  submitCase: (caseId: string) => Promise<KycCase | null>;
  /** Request an upload URL for a document requirement */
  getUploadUrl: (requirementId: string, data: RequestKycUploadUrlData) => Promise<KycUploadUrlResponse | null>;
  /** Complete a document upload */
  completeUpload: (requirementId: string, data: CompleteKycUploadData) => Promise<KycRequirement | null>;
  /** Check if KYC is approved */
  isApproved: boolean;
  /** Check if KYC needs attention */
  needsAttention: boolean;
  /** Get status label for display */
  getStatusLabel: (status: KycCaseStatus) => string;
}

// ============================================================================
// Constants
// ============================================================================

const STATUS_LABELS: Record<KycCaseStatus, string> = {
  DRAFT: 'In Progress',
  PENDING: 'Pending',
  IN_REVIEW: 'Under Review',
  PENDING_ADDITIONAL_INFO: 'More Information Required',
  ESCALATED: 'Escalated',
  APPROVED: 'Approved',
  CONDITIONALLY_APPROVED: 'Conditionally Approved',
  REJECTED: 'Rejected',
  EXPIRED: 'Expired',
  CANCELLED: 'Cancelled',
  SUSPENDED: 'Suspended',
  ARCHIVED: 'Archived',
};

const TERMINAL_STATUSES: KycCaseStatus[] = [
  'REJECTED', 'EXPIRED', 'CANCELLED', 'ARCHIVED',
];

const APPROVED_STATUSES: KycCaseStatus[] = [
  'APPROVED', 'CONDITIONALLY_APPROVED',
];

const ATTENTION_STATUSES: KycCaseStatus[] = [
  'PENDING_ADDITIONAL_INFO', 'REJECTED', 'SUSPENDED',
];

// ============================================================================
// Hook
// ============================================================================

export function useKyc(): UseKycReturn {
  const { getCustomerClient, isAuthenticated, customer } = useCustomerAuth();
  const [cases, setCases] = useState<KycCase[]>([]);
  const [policies, setPolicies] = useState<KycPolicy[]>([]);
  const [requirements, setRequirements] = useState<KycRequirement[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Current active case: first non-terminal case
  const currentCase = useMemo(
    () => cases.find((c) => !TERMINAL_STATUSES.includes(c.status)) ?? null,
    [cases]
  );

  const isApproved = useMemo(
    () => cases.some((c) => APPROVED_STATUSES.includes(c.status)),
    [cases]
  );

  const needsAttention = useMemo(
    () => cases.some((c) => ATTENTION_STATUSES.includes(c.status)),
    [cases]
  );

  const hasAvailablePolicy = policies.length > 0;

  // Split requirements by type
  const byType = useCallback(
    (type: KycRequirementType) => requirements.filter((r) => r.requirementType === type),
    [requirements]
  );
  const documentRequirements = useMemo(() => byType('DOCUMENT'), [byType]);
  const checkRequirements = useMemo(() => byType('VERIFICATION'), [byType]);
  const attributeRequirements = useMemo(() => byType('ATTRIBUTE'), [byType]);

  const completedRequirements = useMemo(
    () => requirements.filter((r) => r.status === 'VERIFIED' || r.status === 'WAIVED').length,
    [requirements]
  );
  const totalRequirements = requirements.length;

  // Fetch policies, cases, and requirements
  const refresh = useCallback(async () => {
    const client = getCustomerClient();
    if (!client) return;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch policies and cases in parallel
      const [policiesRes, casesRes] = await Promise.all([
        client.getKycPolicies('INDIVIDUAL').catch(() => ({ items: [] as KycPolicy[], total: 0 })),
        client.getKycCases().catch(() => ({ items: [] as KycCase[], total: 0 })),
      ]);

      const fetchedPolicies = policiesRes?.items ?? [];
      const fetchedCases = casesRes?.items ?? [];

      // Route Royal may wrap in { data } instead of { items }
      const resolvedPolicies = fetchedPolicies.length > 0
        ? fetchedPolicies
        : ((policiesRes as unknown as { data: KycPolicy[] })?.data ?? []);
      const resolvedCases = fetchedCases.length > 0
        ? fetchedCases
        : ((casesRes as unknown as { data: KycCase[] })?.data ?? []);

      setPolicies(resolvedPolicies);
      setCases(resolvedCases);

      // If there's an active case, fetch its requirements
      const activeCase = resolvedCases.find((c) => !TERMINAL_STATUSES.includes(c.status));
      if (activeCase) {
        try {
          const reqs = await client.getKycRequirements(activeCase.id);
          setRequirements(reqs);
        } catch {
          setRequirements([]);
        }
      } else {
        setRequirements([]);
      }
    } catch (err) {
      console.error('[useKyc] Failed to fetch KYC data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load KYC status');
    } finally {
      setIsLoading(false);
    }
  }, [getCustomerClient]);

  // Load data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      refresh();
    } else {
      setCases([]);
      setPolicies([]);
      setRequirements([]);
    }
  }, [isAuthenticated, refresh]);

  // Start a new KYC case
  const startCase = useCallback(
    async (policyId?: string): Promise<KycCase | null> => {
      const client = getCustomerClient();
      if (!client || !customer) return null;

      // Resolve policy: use provided ID, or auto-select if only one
      const resolvedPolicyId = policyId ?? (policies.length === 1 ? policies[0]?.id : undefined);
      if (!resolvedPolicyId) {
        setError('No KYC policy available. Please contact support.');
        return null;
      }

      setIsSubmitting(true);
      setError(null);

      try {
        const kycCase = await client.createKycCase({
          policyId: resolvedPolicyId,
          subjectType: 'INDIVIDUAL',
          subjectId: customer.id,
        });
        setCases((prev) => [kycCase, ...prev]);

        // Fetch requirements for the new case
        try {
          const reqs = await client.getKycRequirements(kycCase.id);
          setRequirements(reqs);
        } catch {
          setRequirements([]);
        }

        return kycCase;
      } catch (err) {
        console.error('[useKyc] Failed to start KYC case:', err);
        setError(err instanceof Error ? err.message : 'Failed to start verification');
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [getCustomerClient, customer, policies]
  );

  // Submit a KYC case for review
  const submitCase = useCallback(
    async (caseId: string): Promise<KycCase | null> => {
      const client = getCustomerClient();
      if (!client) return null;

      setIsSubmitting(true);
      setError(null);

      try {
        const kycCase = await client.submitKycCase(caseId);
        setCases((prev) => prev.map((c) => (c.id === caseId ? kycCase : c)));
        return kycCase;
      } catch (err) {
        console.error('[useKyc] Failed to submit KYC case:', err);
        setError(err instanceof Error ? err.message : 'Failed to submit for review');
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [getCustomerClient]
  );

  // Request upload URL for a document requirement
  const getUploadUrl = useCallback(
    async (requirementId: string, data: RequestKycUploadUrlData): Promise<KycUploadUrlResponse | null> => {
      const client = getCustomerClient();
      if (!client || !currentCase) return null;

      try {
        return await client.getKycRequirementUploadUrl(currentCase.id, requirementId, data);
      } catch (err) {
        console.error('[useKyc] Failed to get upload URL:', err);
        setError(err instanceof Error ? err.message : 'Failed to prepare upload');
        return null;
      }
    },
    [getCustomerClient, currentCase]
  );

  // Complete a document upload
  const completeUpload = useCallback(
    async (requirementId: string, data: CompleteKycUploadData): Promise<KycRequirement | null> => {
      const client = getCustomerClient();
      if (!client || !currentCase) return null;

      try {
        const updated = await client.completeKycUpload(currentCase.id, requirementId, data);
        setRequirements((prev) =>
          prev.map((r) => (r.id === requirementId ? updated : r))
        );
        return updated;
      } catch (err) {
        console.error('[useKyc] Failed to complete upload:', err);
        setError(err instanceof Error ? err.message : 'Failed to complete upload');
        return null;
      }
    },
    [getCustomerClient, currentCase]
  );

  const getStatusLabel = useCallback(
    (status: KycCaseStatus): string => STATUS_LABELS[status] ?? status,
    []
  );

  return {
    cases,
    currentCase,
    policies,
    hasAvailablePolicy,
    requirements,
    documentRequirements,
    checkRequirements,
    attributeRequirements,
    completedRequirements,
    totalRequirements,
    isLoading,
    isSubmitting,
    error,
    refresh,
    startCase,
    submitCase,
    getUploadUrl,
    completeUpload,
    isApproved,
    needsAttention,
    getStatusLabel,
  };
}

export default useKyc;
